import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import type { Tone } from './badge';

export function Card({ className, children, padded = true, onClick }: {
  className?: string;
  children: ReactNode;
  padded?: boolean;
  onClick?: () => void;
}) {
  return (
    <section
      onClick={onClick}
      className={cn(
        // min-w-0: a grid item may not shrink below its content otherwise, and a table inside would widen the page.
        'min-w-0 rounded-2xl border border-line bg-surface shadow-card animate-fade-in',
        padded && 'p-5',
        onClick && 'cursor-pointer transition-[border-color,transform] hover:border-line-strong',
        className,
      )}
    >
      {children}
    </section>
  );
}

export function CardHeader({ title, subtitle, icon, action, className }: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-4 flex items-start gap-3', className)}>
      {icon && <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent-ink">{icon}</div>}
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-bold text-ink">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs leading-5 text-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

const ICON_TONE: Record<Tone, string> = {
  accent:  'bg-accent-soft text-accent-ink',
  ok:      'bg-ok-soft text-ok',
  warn:    'bg-warn-soft text-warn',
  danger:  'bg-danger-soft text-danger',
  neutral: 'bg-surface-2 text-muted',
};

/** A KPI tile: one number, one label, an optional footnote and an optional delta. */
export function Stat({ label, value, hint, icon, tone = 'accent', delta, onClick }: {
  label: string;
  value: string | number;
  hint?: string;
  icon: ReactNode;
  tone?: Tone;
  /** Signed change against a previous period, already formatted. */
  delta?: { text: string; good: boolean | null };
  onClick?: () => void;
}) {
  return (
    <Card padded={false} onClick={onClick} className="flex min-w-[160px] flex-1 flex-col gap-3 p-4">
      <div className="flex items-center justify-between">
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl', ICON_TONE[tone])}>{icon}</div>
        {delta && (
          <span className={cn('rounded-md px-1.5 py-0.5 text-[11px] font-semibold',
            delta.good === null ? 'bg-surface-2 text-muted' : delta.good ? 'bg-ok-soft text-ok' : 'bg-danger-soft text-danger')}>
            {delta.text}
          </span>
        )}
      </div>
      <div>
        <div className="text-2xl font-bold tracking-tight text-ink tabular-nums">{value}</div>
        <div className="mt-0.5 text-xs text-muted">{label}</div>
        {hint && <div className="mt-1 text-[11px] leading-4 text-faint">{hint}</div>}
      </div>
    </Card>
  );
}

/** A bar of `value` against `max` in the brand hue; over the limit it turns to the danger tone. */
export function Meter({ value, max, label }: { value: number; max: number | null; label: string }) {
  const unlimited = max === null;
  const ratio = unlimited || !max ? 0 : Math.min(1, value / max);
  const over = !unlimited && !!max && value > max;
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-xs">
        <span className="text-muted">{label}</span>
        <span className={cn('font-semibold tabular-nums', over ? 'text-danger' : 'text-ink')}>
          {value}{unlimited ? ' / ∞' : ` / ${max}`}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-3">
        <div className={cn('h-full rounded-full transition-[width]', over ? 'bg-danger' : 'bg-accent')} style={{ width: unlimited ? '4%' : `${Math.max(2, ratio * 100)}%` }} />
      </div>
    </div>
  );
}

/** A label + value pair for detail panels. */
export function Prop({ label, children, mono }: { label: string; children: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 text-sm">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className={cn('min-w-0 break-words text-right text-ink', mono && 'break-all font-mono text-xs')}>{children}</dd>
    </div>
  );
}
